<script lang="ts" setup>
import {ref, computed, getCurrentInstance} from "vue";
import {sizes, animate} from "./data";
import {Motion, Presence} from "motion/vue";
import DxButton from "devextreme-vue"
import traducao from "@/localization/vistra.geral.json";

const app = getCurrentInstance()
const {$vistra, $route, $store, $http} = app!.appContext.config.globalProperties

// tradução da tela
$vistra.carregaTraducao([traducao]);

type Props = {
  size?: "md" | "lg" | "full";
  back?: boolean;
  title?: string;
  noPadding?: boolean;
  closeOnClickOutside?: boolean;
  showCloseButton?: boolean;
  id?: string,
  destruirAoFechar?: boolean,
  showHeaderDrawer?: boolean,
  usarNovoUX?: boolean,
  botoesAdicionais: boolean,
  showMenuVistra: boolean,
  confirmarBotao: Function,
  cancelaBotao: Function,
  showFullscreenButton: boolean
};

type EmitHandles = {
  (e: "onClose"): void;
  (e: "onBack"): void;
  (e: "onFullscreen", size: string): void;
};

const show = ref(false);

const emit = defineEmits<EmitHandles>();

const toggle = () => {
  if (show.value) emit("onClose");
  show.value = !show.value;
};

const close = () => {
  toggle();
};

const fullscreen = () => {
  emit("onFullscreen", props.size)
}

defineExpose({
  toggle,
  isActive: show
});

const props = withDefaults(defineProps<Props>(), {
  size: "md",
  back: false,
  noPadding: false,
  closeOnClickOutside: true,
  showCloseButton: true,
  destruirAoFechar: true,
  showHeaderDrawer: true,
  usarNovoUX: true,
  botoesAdicionais: false,
  showMenuVistra: false,
  confirmarBotao: () => {
  },
  cancelaBotao: () => {
  },
  showFullscreenButton: false
});

const mostraIf = computed(() => !props.destruirAoFechar || show.value)
const mostraShow = computed(() => props.destruirAoFechar || show.value)
const novoUX = computed(() => {
  let baseClass = props.usarNovoUX ? 'drawer-layout ' + sizes[props.size] : sizes[props.size]

  if (props.showMenuVistra) {
    baseClass += ' top-20';
  } else {
    baseClass += ' top-0';
  }
  return baseClass;
})
</script>

<template>
    <div
        v-if="!showMenuVistra"
        @click="props.closeOnClickOutside ? close() : undefined"
        :class="
        show ? 'pointer-events-auto opacity-100' : 'pointer-events-none opacity-0'
      "
        class="w-full h-screen fixed z-[99998] top-0 left-0 bg-black/50 transition-all duration-300 ease-in-out"
    ></div>
  
    <Presence exitBeforeEnter>
      <Motion
          v-show="mostraShow"
          v-if="mostraIf"
          :exit="animate.initial"
          class="drawer  fixed z-[99999] h-screen bg-gray-50 dark:bg-gray-900"
          :class="novoUX"
          :initial="animate.initial"
          :animate="animate.animate"
          :id="props.id"
      >
        <div
            v-if="props.showHeaderDrawer"
            :class="props.botoesAdicionais ? 'justify-between drawer-head w-full flex items-center pt-1 pb-1 pr-6 pl-6 border-b border-solid border-b-gray-300 dark:border-b-gray-700' : 'drawer-head w-full flex items-center p-6 border-b border-solid border-b-gray-300 dark:border-b-gray-700'"
        >
          <slot name="icon"></slot>
  
          <div class="flex items-center">
            <button v-if="props.back" type="button" @click="emit('onBack')">
              <i
                  role="presentation"
                  class="dx-icon-arrowleft mr-3 cursor-pointer text-sm text-black dark:text-white transition-all hover:text-primary-500"
              ></i>
            </button>
  
            <h4 class="text-black dark:text-white">{{ props.title }}</h4>
          </div>
  
          <div class="ml-auto">
            <button type="button" @click="fullscreen()" class="mr-3" v-if="props.showFullscreenButton">
              <i  v-if="props.size != 'full'"
                  role="presentation"
                  class="fa-light fa-expand cursor-pointer text-md text-black dark:text-white transition-all hover:text-primary-500"
              ></i>
              <i  v-else
                  role="presentation"
                  class="fa-light fa-compress cursor-pointer text-md text-black dark:text-white transition-all hover:text-primary-500"
              ></i>
            </button>
            <button type="button" @click="close()"  v-if="props.showCloseButton">
              <i
                  role="presentation"
                  class="dx-icon-close cursor-pointer text-lg text-black dark:text-white transition-all hover:text-primary-500"
              ></i>
            </button>
          </div>
          
  
          <div class="flex items-center justify-end" v-if="props.botoesAdicionais">
            <button type="button" @click="props.confirmarBotao()"
                    class="botaoAdicional dx-button-default p-2 mr-4 text-primary-500 hover:bg-primary-100 pointer rounded">
  
              <i class="fa-solid fa-check"></i>
              {{ $vistra.globalize("btn-confirmar").toUpperCase() }}
            </button>
  
            <button type="button" @click="props.cancelaBotao()"
                    class="botaoAdicional dx-button-default p-2 text-primary-500 hover:bg-primary-100 pointer rounded">
  
              <i class="fa-regular fa-rotate-left"></i>
              {{ $vistra.globalize("btn-cancelar").toUpperCase() }}
            </button>
          </div>
        </div>
  
        <div
            id="containerPrincipal"
            class="drawer-content flex flex-col overflow-y-auto novo-ux "
            :class="{ 
            'p-6': !props.noPadding, 
            'h-[calc(100vh)]': !props.showToolbar,
            'h-[calc(100vh-5rem)]': props.showToolbar 
          }"
        >
          <slot></slot>
        </div>
      </Motion>
    </Presence>
</template>

<style lang="scss">

.drawer-layout {
  @import "@/styles/base.scss";
}

@media (max-width: 440px) {
  .drawer {
    @apply max-w-full w-full;
  }
}

.botaoAdicional {
  font-family: 'QuicksandFallback', 'Quicksand';
  font-size: 13px;
}

#containerPrincipal {
  padding-bottom: 100px !important;
}
</style>
